<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[1050] flex items-center justify-center p-2.5 sm:p-4 bg-slate-900/60 backdrop-blur-sm"
  >
    <div
      class="relative w-full max-w-4xl bg-white dark:bg-[#111C30] rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200/80 dark:border-[#263449] overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[88vh]"
    >
      <!-- Modal Header -->
      <div
        class="px-4 sm:px-6 py-3.5 sm:py-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between flex-shrink-0 gap-2"
      >
        <div class="flex items-center space-x-2.5 sm:space-x-3 min-w-0">
          <div
            class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 flex-shrink-0 text-indigo-400"
          >
            <Wallet class="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div class="min-w-0 truncate">
            <h3 class="text-sm sm:text-lg font-black tracking-tight text-white truncate">
              Student Fee Account
            </h3>
            <p class="text-[11px] sm:text-xs text-indigo-200/80 font-medium truncate">
              Lifetime realization & multi-session class ledger
            </p>
          </div>
        </div>
        <div class="flex items-center space-x-1.5 sm:space-x-2 flex-shrink-0">
          <button
            @click="$emit('configure', student)"
            class="px-2.5 sm:px-3 py-1.5 text-xs font-bold bg-white/10 hover:bg-white/20 text-white rounded-xl border border-white/20 transition flex items-center gap-1.5 cursor-pointer"
          >
            <Sliders class="w-3.5 h-3.5" />
            <span class="hidden sm:inline">Configure</span>
          </button>
          <button
            @click="$emit('close')"
            class="p-1.5 sm:p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition cursor-pointer"
          >
            <X class="w-5 h-5" />
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="p-12 text-center flex-1 flex flex-col items-center justify-center">
        <AppLoader
          variant="card"
          size="md"
          message="Loading Student Fee Account..."
          subtext="Calculating lifetime realization and multi-session ledger"
          color="indigo"
        />
      </div>

      <!-- Main Content Area -->
      <div v-else-if="student" class="flex-1 overflow-y-auto overscroll-contain p-3.5 sm:p-6 space-y-4 sm:space-y-5">
        <!-- 1. Top Identity & Profile Banner -->
        <div class="p-3.5 sm:p-4 bg-slate-50 dark:bg-[#18243A] rounded-2xl border border-slate-200/80 dark:border-[#263449] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div class="flex items-center space-x-3 sm:space-x-4 min-w-0">
            <div class="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-white dark:bg-[#111C30] shadow-xs border border-slate-200/80 dark:border-[#263449] flex items-center justify-center overflow-hidden flex-shrink-0">
              <img
                v-if="student.photo"
                :src="student.photo"
                alt="Student Photo"
                class="w-full h-full object-cover"
              />
              <User v-else class="w-6 h-6 text-indigo-500" />
            </div>
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <h4 class="text-sm sm:text-base font-black text-slate-900 dark:text-[#F1F5F9] truncate">
                  {{ student.name }}
                </h4>
                <span
                  class="px-2 py-0.5 text-[9px] font-extrabold uppercase rounded-full"
                  :class="
                    student.status === 'active'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                      : 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                  "
                >
                  {{ student.status || "active" }}
                </span>
              </div>
              <p class="text-xs text-slate-500 dark:text-[#A6B4C8] font-medium mt-0.5 truncate">
                Class: <strong class="text-slate-800 dark:text-[#F1F5F9]">{{ student.class?.name || "N/A" }}</strong>
                <span v-if="student.class?.section"> ({{ student.class.section }})</span>
                • Roll: <strong class="text-slate-800 dark:text-[#F1F5F9] font-mono">{{ student.rollNumber || "N/A" }}</strong>
                <span v-if="student.attendanceID"> • ID: {{ student.attendanceID }}</span>
              </p>
              <div class="flex flex-wrap items-center gap-x-3 text-[11px] text-slate-400 dark:text-[#7E8DA5] mt-0.5">
                <span>Session: <strong class="text-indigo-600 dark:text-indigo-400">{{ feeStructure?.academicSession || student.session || "Current" }}</strong></span>
                <span v-if="computedLifetime.enrolledYear">Enrolled: <strong class="text-slate-700 dark:text-slate-300">{{ computedLifetime.enrolledYear }}</strong></span>
                <span v-if="student.phone">Phone: {{ student.phone }}</span>
              </div>
            </div>
          </div>

          <!-- Quick Action Buttons -->
          <div class="flex items-center gap-2 w-full sm:w-auto">
            <button
              @click="$emit('record-payment', { student, feeStructure, monthlyRecords })"
              class="flex-1 sm:flex-initial px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs transition flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <PlusCircle class="w-3.5 h-3.5" />
              <span>Record Payment</span>
            </button>
            <button
              @click="$emit('open-adjustment', student)"
              class="flex-1 sm:flex-initial px-3.5 py-2 bg-white dark:bg-[#111C30] hover:bg-slate-100 dark:hover:bg-[#172235] text-indigo-600 dark:text-indigo-400 text-xs font-bold rounded-xl border border-slate-200/80 dark:border-[#263449] transition flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
            >
              <Scale class="w-3.5 h-3.5" />
              <span>+ Debit / Credit</span>
            </button>
          </div>
        </div>

        <!-- Carried-Forward Previous Class Dues Banner (Requirement 1) -->
        <div
          v-if="computedPreviousClassDues.hasPreviousDues"
          class="bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700/60 p-4 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs"
        >
          <div class="flex items-start gap-3">
            <div class="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 flex items-center justify-center shrink-0">
              <History class="w-4 h-4" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h4 class="text-xs font-black uppercase tracking-wider text-amber-900 dark:text-amber-200">
                  Carried-Forward Previous Class Dues
                </h4>
                <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-200 dark:bg-amber-800/80 text-amber-950 dark:text-amber-100">
                  Arrears
                </span>
              </div>
              <p class="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                {{ computedPreviousClassDues.message || `Carried forward from ${computedPreviousClassDues.previousClassName || 'Previous Class'}: Admission fee ₹${(computedPreviousClassDues.unpaidAdmissionFee || 0).toLocaleString('en-IN')} not paid, ${computedPreviousClassDues.unpaidMonthsCount || 0} months tuition fees (₹${(computedPreviousClassDues.unpaidMonthlyFee || 0).toLocaleString('en-IN')}) not paid.` }}
              </p>
            </div>
          </div>
          <button
            @click="$emit('record-payment', { student, feeStructure, monthlyRecords, previousClassDues: computedPreviousClassDues, category: 'previous_dues' })"
            class="px-3.5 py-2 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl shadow-xs transition flex items-center gap-1.5 shrink-0 cursor-pointer"
          >
            <CreditCard class="w-3.5 h-3.5" />
            <span>Pay Previous Dues (₹{{ (computedPreviousClassDues.balance || 0).toLocaleString('en-IN') }})</span>
          </button>
        </div>

        <!-- 2. Outstanding Fee Summary (5 Cards matching Section 3 Requirement) -->
        <div class="bg-gradient-to-br from-indigo-50/60 via-purple-50/40 to-slate-50 dark:from-[#131D33] dark:via-[#111C30] dark:to-[#0F172A] p-4 rounded-2xl border border-indigo-100/80 dark:border-[#263449] space-y-3">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
            <div class="flex items-center gap-2">
              <span class="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-[#F1F5F9] flex items-center gap-1.5">
                <Award class="w-4 h-4 text-indigo-500" />
                Outstanding Fee Summary
              </span>
              <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950/70 text-indigo-800 dark:text-indigo-300">
                Lifetime Realization
              </span>
            </div>
            <span class="text-[11px] text-slate-500 dark:text-[#A6B4C8] font-medium">
              From enrollment ({{ computedLifetime.enrolledYear || 'Joined' }}) to present • {{ computedSessionBreakdown.length }} session(s)
            </span>
          </div>

          <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            <!-- 1. Previous Class Outstanding -->
            <div class="bg-amber-500/10 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700/60 p-3 rounded-xl flex flex-col justify-between">
              <div class="flex items-center justify-between text-amber-700 dark:text-amber-400 mb-1">
                <span class="text-[9px] font-bold uppercase tracking-wider">Previous Class Outstanding</span>
                <History class="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
              </div>
              <div class="text-sm sm:text-base font-black text-amber-900 dark:text-amber-200 font-mono">
                ₹{{ (computedLifetime.previousClassOutstanding || computedPreviousClassDues.balance || 0).toLocaleString("en-IN") }}
              </div>
              <span class="text-[9px] text-amber-700 dark:text-amber-400 block mt-0.5 truncate">
                Carried-forward arrears
              </span>
            </div>

            <!-- 2. Current Class Fees -->
            <div class="bg-indigo-500/10 dark:bg-indigo-950/30 border border-indigo-300 dark:border-indigo-700/60 p-3 rounded-xl flex flex-col justify-between">
              <div class="flex items-center justify-between text-indigo-600 dark:text-indigo-400 mb-1">
                <span class="text-[9px] font-bold uppercase tracking-wider">Current Class Fees</span>
                <GraduationCap class="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 shrink-0" />
              </div>
              <div class="text-sm sm:text-base font-black text-indigo-900 dark:text-indigo-200 font-mono">
                ₹{{ (computedLifetime.currentClassFees || 0).toLocaleString("en-IN") }}
              </div>
              <span class="text-[9px] text-indigo-600 dark:text-indigo-400 block mt-0.5 truncate">
                Admission + tuition
              </span>
            </div>

            <!-- 3. Total Fees Due -->
            <div class="bg-white dark:bg-[#18243A] border border-slate-200/80 dark:border-[#263449] p-3 rounded-xl flex flex-col justify-between shadow-2xs">
              <div class="flex items-center justify-between text-slate-500 dark:text-[#A6B4C8] mb-1">
                <span class="text-[9px] font-bold uppercase tracking-wider">Total Fees Due</span>
                <Layers class="w-3.5 h-3.5 text-slate-500 dark:text-[#A6B4C8] shrink-0" />
              </div>
              <div class="text-sm sm:text-base font-black text-slate-900 dark:text-[#F1F5F9] font-mono">
                ₹{{ (computedLifetime.grandTotalExpected || 0).toLocaleString("en-IN") }}
              </div>
              <span class="text-[9px] text-slate-500 dark:text-[#A6B4C8] block mt-0.5 truncate">
                Previous + Current dues
              </span>
            </div>

            <!-- 4. Total Amount Paid -->
            <div class="bg-emerald-500/10 dark:bg-emerald-950/30 border border-emerald-300 dark:border-emerald-700/60 p-3 rounded-xl flex flex-col justify-between">
              <div class="flex items-center justify-between text-emerald-600 dark:text-emerald-400 mb-1">
                <span class="text-[9px] font-bold uppercase tracking-wider">Total Amount Paid</span>
                <CheckCircle2 class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
              </div>
              <div class="text-sm sm:text-base font-black text-emerald-600 dark:text-emerald-400 font-mono">
                ₹{{ (computedLifetime.grandTotalPaid || 0).toLocaleString("en-IN") }}
              </div>
              <span class="text-[9px] text-emerald-600 dark:text-emerald-400 block mt-0.5 truncate font-semibold">
                {{ computedLifetime.collectionRate }}% Realized
              </span>
            </div>

            <!-- 5. Remaining Balance -->
            <div class="col-span-2 sm:col-span-1 bg-rose-500/10 dark:bg-rose-950/30 border border-rose-300 dark:border-rose-700/60 p-3 rounded-xl flex flex-col justify-between">
              <div class="flex items-center justify-between text-rose-600 dark:text-rose-400 mb-1">
                <span class="text-[9px] font-bold uppercase tracking-wider">Remaining Balance</span>
                <AlertCircle class="w-3.5 h-3.5 text-rose-600 dark:text-rose-400 shrink-0" />
              </div>
              <div
                class="text-sm sm:text-base font-black font-mono"
                :class="(computedLifetime.grandTotalOutstanding || 0) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'"
              >
                ₹{{ (computedLifetime.grandTotalOutstanding || 0).toLocaleString("en-IN") }}
              </div>
              <span
                class="text-[9px] font-bold block mt-0.5 truncate"
                :class="(computedLifetime.grandTotalOutstanding || 0) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'"
              >
                {{ (computedLifetime.grandTotalOutstanding || 0) > 0 ? 'Outstanding dues' : '✓ Cleared' }}
              </span>
            </div>
          </div>
        </div>

        <!-- 3. Navigation View Switcher (Simple, Clean Pills) -->
        <div class="flex items-center gap-1.5 border-b border-slate-100 dark:border-[#1E293B] pb-2 overflow-x-auto whitespace-nowrap scrollbar-none">
          <button
            @click="activeView = 'journey'"
            :class="[
              'px-3 py-1.5 text-xs font-black rounded-xl transition cursor-pointer flex items-center gap-1.5',
              activeView === 'journey'
                ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-xs'
                : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]'
            ]"
          >
            <CalendarRange class="w-3.5 h-3.5" />
            <span>Class & Session Journey</span>
            <span class="text-[10px] px-1.5 py-0.2 rounded-md font-mono" :class="activeView === 'journey' ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900' : 'bg-slate-200 dark:bg-[#111C30]'">
              {{ computedSessionBreakdown.length }}
            </span>
          </button>

          <button
            @click="activeView = 'monthly'"
            :class="[
              'px-3 py-1.5 text-xs font-black rounded-xl transition cursor-pointer flex items-center gap-1.5',
              activeView === 'monthly'
                ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-xs'
                : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]'
            ]"
          >
            <CalendarDays class="w-3.5 h-3.5" />
            <span>Monthly Cycles</span>
            <span class="text-[10px] px-1.5 py-0.2 rounded-md font-mono" :class="activeView === 'monthly' ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900' : 'bg-slate-200 dark:bg-[#111C30]'">
              {{ monthlyRecords.length }}
            </span>
          </button>

          <button
            @click="activeView = 'adjustments'"
            :class="[
              'px-3 py-1.5 text-xs font-black rounded-xl transition cursor-pointer flex items-center gap-1.5',
              activeView === 'adjustments'
                ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-xs'
                : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]'
            ]"
          >
            <Scale class="w-3.5 h-3.5" />
            <span>Debits & Credits</span>
            <span class="text-[10px] px-1.5 py-0.2 rounded-md font-mono" :class="activeView === 'adjustments' ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900' : 'bg-slate-200 dark:bg-[#111C30]'">
              {{ adjustments.length }}
            </span>
          </button>

          <button
            @click="activeView = 'transactions'"
            :class="[
              'px-3 py-1.5 text-xs font-black rounded-xl transition cursor-pointer flex items-center gap-1.5',
              activeView === 'transactions'
                ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-xs'
                : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]'
            ]"
          >
            <Receipt class="w-3.5 h-3.5" />
            <span>Payment Receipts</span>
            <span class="text-[10px] px-1.5 py-0.2 rounded-md font-mono" :class="activeView === 'transactions' ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900' : 'bg-slate-200 dark:bg-[#111C30]'">
              {{ transactions.length }}
            </span>
          </button>
        </div>

        <!-- 4. VIEW 1: CLASS & SESSION JOURNEY (Section 3 Requirement: Grouped Table) -->
        <div v-if="activeView === 'journey'" class="space-y-4">
          <div class="flex items-center justify-between">
            <h5 class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8] uppercase tracking-wider flex items-center gap-1.5">
              <CalendarRange class="w-4 h-4 text-indigo-500" />
              Academic Class & Session Fee History
            </h5>
            <span class="text-[11px] text-slate-400 dark:text-[#7E8DA5]">
              Realization details per academic session
            </span>
          </div>

          <div class="space-y-4">
            <div
              v-for="sItem in computedSessionFeeHistory"
              :key="sItem.session + '-' + sItem.className"
              class="rounded-2xl border overflow-hidden transition-all shadow-2xs"
              :class="
                sItem.isCurrent
                  ? 'border-indigo-200 dark:border-indigo-900/60 bg-white dark:bg-[#18243A]'
                  : 'border-amber-200 dark:border-amber-900/50 bg-amber-50/20 dark:bg-amber-950/20'
              "
            >
              <!-- Session Header -->
              <div
                class="px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b"
                :class="
                  sItem.isCurrent
                    ? 'bg-indigo-50/70 dark:bg-indigo-950/40 border-indigo-100 dark:border-indigo-900/40'
                    : 'bg-amber-100/60 dark:bg-amber-900/30 border-amber-200/70 dark:border-amber-800/40'
                "
              >
                <div class="flex items-center gap-2.5">
                  <div
                    class="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                    :class="sItem.isCurrent ? 'bg-indigo-600 text-white' : 'bg-amber-600 text-white'"
                  >
                    <History v-if="!sItem.isCurrent" class="w-3.5 h-3.5" />
                    <GraduationCap v-else class="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <h4 class="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-white">
                      {{ sItem.className }} – {{ sItem.isCurrent ? 'Current Academic Session' : 'Previous Academic Session' }}
                    </h4>
                    <span class="text-[10px] text-slate-500 dark:text-slate-400 font-mono">
                      Session: {{ sItem.session }}
                    </span>
                  </div>
                </div>

                <div class="flex items-center gap-2 self-start sm:self-auto">
                  <span
                    v-if="!sItem.isCurrent"
                    class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-200 dark:bg-amber-800/80 text-amber-900 dark:text-amber-100"
                  >
                    Previous Class
                  </span>
                  <span
                    v-else
                    class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-200 dark:bg-indigo-800/80 text-indigo-900 dark:text-indigo-100"
                  >
                    Current Class
                  </span>
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[11px] font-mono font-bold capitalize"
                    :class="
                      sItem.totalPending === 0
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                        : sItem.totalPaid > 0
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                        : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                    "
                  >
                    {{ sItem.totalPending === 0 ? 'Paid' : (sItem.totalPaid > 0 ? 'Partial' : 'Unpaid') }}
                  </span>
                </div>
              </div>

              <!-- Session Items Table -->
              <div class="overflow-x-auto">
                <table class="w-full text-left text-xs min-w-[500px]">
                  <thead class="bg-slate-50 dark:bg-[#111C30]/50 text-slate-600 dark:text-[#A6B4C8] uppercase font-bold text-[10px] border-b border-slate-200/70 dark:border-[#263449]">
                    <tr>
                      <th class="py-2.5 px-4">Fee Description</th>
                      <th class="py-2.5 px-3 text-right">Amount</th>
                      <th class="py-2.5 px-3 text-right">Paid</th>
                      <th class="py-2.5 px-3 text-right">Pending</th>
                      <th class="py-2.5 px-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 dark:divide-[#263449] font-medium">
                    <tr
                      v-for="(itm, idx) in (sItem.items || [])"
                      :key="idx"
                      class="hover:bg-slate-50/60 dark:hover:bg-[#18243A]/40 transition"
                    >
                      <td class="py-2.5 px-4 font-bold text-slate-800 dark:text-[#F1F5F9]">
                        {{ itm.feeDescription }}
                      </td>
                      <td class="py-2.5 px-3 text-right font-mono font-semibold text-slate-700 dark:text-slate-300">
                        ₹{{ (itm.amount || 0).toLocaleString("en-IN") }}
                      </td>
                      <td class="py-2.5 px-3 text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                        ₹{{ (itm.paid || 0).toLocaleString("en-IN") }}
                      </td>
                      <td class="py-2.5 px-3 text-right font-mono font-bold" :class="(itm.pending || 0) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'">
                        ₹{{ (itm.pending || 0).toLocaleString("en-IN") }}
                      </td>
                      <td class="py-2.5 px-3 text-center">
                        <span
                          class="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase inline-block font-mono"
                          :class="
                            itm.status === 'Paid'
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                              : itm.status === 'Partial'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                              : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                          "
                        >
                          {{ itm.status }}
                        </span>
                      </td>
                    </tr>
                    <!-- Subtotal Footer Row -->
                    <tr class="bg-slate-50 dark:bg-[#111C30]/80 font-extrabold border-t-2 border-slate-200 dark:border-[#263449]">
                      <td class="py-2.5 px-4 text-slate-900 dark:text-white uppercase text-[11px]">
                        Total
                      </td>
                      <td class="py-2.5 px-3 text-right font-mono text-slate-900 dark:text-white">
                        ₹{{ (sItem.totalAmount || 0).toLocaleString("en-IN") }}
                      </td>
                      <td class="py-2.5 px-3 text-right font-mono text-emerald-600 dark:text-emerald-400">
                        ₹{{ (sItem.totalPaid || 0).toLocaleString("en-IN") }}
                      </td>
                      <td class="py-2.5 px-3 text-right font-mono" :class="(sItem.totalPending || 0) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'">
                        ₹{{ (sItem.totalPending || 0).toLocaleString("en-IN") }}
                      </td>
                      <td class="py-2.5 px-3 text-center">
                        <span
                          class="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase font-mono"
                          :class="(sItem.totalPending || 0) === 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'"
                        >
                          {{ (sItem.totalPending || 0) === 0 ? 'Paid' : 'Pending' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Grand Outstanding Banner -->
            <div class="mt-4 p-4 rounded-xl bg-slate-900 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-md">
              <div>
                <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Overall Realization Balance</span>
                <div class="text-lg font-black text-white flex items-center gap-2">
                  <span>Grand Outstanding:</span>
                  <span class="text-rose-400 font-mono">₹{{ (computedLifetime.grandTotalOutstanding || 0).toLocaleString("en-IN") }}</span>
                </div>
              </div>
              <div class="flex items-center gap-2 text-xs text-slate-300 font-mono">
                <span class="bg-white/10 px-2.5 py-1 rounded-lg">Prev Arrears: ₹{{ (computedLifetime.previousClassOutstanding || computedPreviousClassDues.balance || 0).toLocaleString("en-IN") }}</span>
                <span class="text-slate-500">+</span>
                <span class="bg-white/10 px-2.5 py-1 rounded-lg">Current Class: ₹{{ (computedLifetime.currentClassFees || 0).toLocaleString("en-IN") }}</span>
              </div>
            </div>

            <!-- Promotion History Timeline in StudentFeeModal -->
            <div
              v-if="promotionHistory && promotionHistory.length > 0"
              class="p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#18243A] space-y-3"
            >
              <div class="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-[#263449]">
                <div class="flex items-center gap-2">
                  <History class="w-4 h-4 text-indigo-500" />
                  <span class="font-bold text-xs uppercase tracking-wider text-slate-800 dark:text-[#F1F5F9]">
                    Promotion Audit History
                  </span>
                </div>
                <span class="text-[11px] text-slate-400 font-mono">
                  {{ promotionHistory.length }} Record{{ promotionHistory.length > 1 ? 's' : '' }}
                </span>
              </div>

              <div class="space-y-2.5 text-xs">
                <div
                  v-for="prom in promotionHistory"
                  :key="prom._id"
                  class="p-3 rounded-xl border border-slate-200/60 dark:border-[#263449] bg-slate-50/50 dark:bg-[#111C30]/50 space-y-1.5"
                >
                  <div class="flex items-center justify-between">
                    <span class="font-extrabold text-slate-800 dark:text-white flex items-center gap-1.5">
                      <GraduationCap class="w-3.5 h-3.5 text-indigo-500" />
                      <span>{{ prom.fromClassName || 'Class' }} ({{ prom.fromSession }}) &rarr; {{ prom.toClassName || 'Class' }} ({{ prom.toSession }})</span>
                    </span>
                    <span class="text-[10px] text-slate-400 font-mono">
                      {{ new Date(prom.promotionDate).toLocaleDateString("en-IN") }}
                    </span>
                  </div>
                  <div class="grid grid-cols-3 gap-2 text-[11px] font-mono pt-1 border-t border-slate-200/40 dark:border-[#263449]">
                    <div>
                      <span class="text-slate-400 text-[10px] block">Carried Dues</span>
                      <strong class="text-amber-600 dark:text-amber-400">₹{{ (prom.financialSnapshot?.carriedForwardPreviousDues || 0).toLocaleString("en-IN") }}</strong>
                    </div>
                    <div>
                      <span class="text-slate-400 text-[10px] block">New Class Fees</span>
                      <strong class="text-indigo-600 dark:text-indigo-400">₹{{ (prom.financialSnapshot?.newClassFeesExpected || 0).toLocaleString("en-IN") }}</strong>
                    </div>
                    <div>
                      <span class="text-slate-400 text-[10px] block">Initial Outstanding</span>
                      <strong class="text-slate-900 dark:text-white">₹{{ (prom.financialSnapshot?.initialTotalOutstanding || 0).toLocaleString("en-IN") }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 5. VIEW 2: MONTHLY BILLING CYCLES TABLE -->
        <div v-else-if="activeView === 'monthly'" class="space-y-3">
          <div class="flex items-center justify-between">
            <h5 class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8] uppercase tracking-wider flex items-center gap-1.5">
              <CalendarDays class="w-4 h-4 text-purple-500" />
              Monthly Billing Obligations
            </h5>
            <span class="text-xs text-slate-400 dark:text-[#7E8DA5]">
              {{ monthlyRecords.length }} cycle{{ monthlyRecords.length > 1 ? "s" : "" }}
            </span>
          </div>

          <div class="border border-slate-200/80 dark:border-[#263449] rounded-2xl overflow-hidden bg-white dark:bg-[#18243A] shadow-xs">
            <div class="overflow-x-auto">
              <table class="w-full text-left text-xs min-w-[620px]">
                <thead class="bg-slate-50 dark:bg-[#111C30] text-slate-600 dark:text-[#A6B4C8] uppercase font-bold text-[11px] border-b border-slate-200/80 dark:border-[#263449]">
                  <tr>
                    <th class="py-3 px-4">Billing Month</th>
                    <th class="py-3 px-4">Due Date</th>
                    <th class="py-3 px-4 text-right">Fee Due</th>
                    <th class="py-3 px-4 text-right">Amount Paid</th>
                    <th class="py-3 px-4 text-right">Balance Left</th>
                    <th class="py-3 px-4 text-center">Status</th>
                    <th class="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 dark:divide-[#263449] font-medium">
                  <tr
                    v-for="rec in monthlyRecords"
                    :key="rec._id"
                    :class="rec.status === 'overdue' ? 'bg-rose-50/20 dark:bg-rose-950/10' : 'hover:bg-slate-50/70 dark:hover:bg-[#1A2840]/60'"
                  >
                    <td class="py-3 px-4 font-bold text-slate-800 dark:text-[#F1F5F9]">
                      {{ rec.monthName }} {{ rec.year }}
                    </td>
                    <td class="py-3 px-4 text-slate-500 dark:text-[#A6B4C8]">
                      {{ formatDateShort(rec.dueDate) }}
                    </td>
                    <td class="py-3 px-4 text-right font-mono font-bold text-slate-800 dark:text-[#F1F5F9]">
                      ₹{{ (rec.feeDue || 0).toLocaleString("en-IN") }}
                    </td>
                    <td class="py-3 px-4 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                      ₹{{ (rec.amountPaid || 0).toLocaleString("en-IN") }}
                    </td>
                    <td
                      class="py-3 px-4 text-right font-mono font-black"
                      :class="rec.balance > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'"
                    >
                      ₹{{ (rec.balance || 0).toLocaleString("en-IN") }}
                    </td>
                    <td class="py-3 px-4 text-center">
                      <span
                        class="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase inline-block"
                        :class="
                          rec.status === 'paid'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                            : rec.status === 'partial'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                            : rec.status === 'overdue'
                            ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 font-black'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300'
                        "
                      >
                        {{ rec.status }}
                      </span>
                    </td>
                    <td class="py-3 px-4 text-right">
                      <button
                        v-if="rec.balance > 0"
                        @click="$emit('record-payment', { student, feeStructure, monthlyRecords, category: 'monthly', monthlyFeeRecordId: rec._id })"
                        class="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition shadow-xs cursor-pointer"
                      >
                        {{ rec.amountPaid > 0 ? "Pay Left" : "Pay Due" }}
                      </button>
                      <button
                        v-else
                        @click="viewMonthlyReceipt(rec)"
                        class="px-2.5 py-1 text-slate-600 dark:text-[#A6B4C8] hover:text-indigo-600 dark:hover:text-indigo-400 text-xs font-bold rounded-lg transition cursor-pointer"
                      >
                        Receipt
                      </button>
                    </td>
                  </tr>

                  <tr v-if="monthlyRecords.length === 0">
                    <td colspan="7" class="py-8 text-center text-slate-400 text-xs">
                      No monthly billing records found.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- 6. VIEW 3: DEBITS & CREDITS TABLE -->
        <div v-else-if="activeView === 'adjustments'" class="space-y-3">
          <div class="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h5 class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8] uppercase tracking-wider flex items-center gap-1.5">
                <Scale class="w-4 h-4 text-indigo-500" />
                Additional Item Debits & Concessions
              </h5>
              <span class="text-[11px] text-slate-400 dark:text-[#7E8DA5]">
                Charges (ID Card, Badge, Uniform) & credits applied
              </span>
            </div>
            <button
              @click="$emit('open-adjustment', student)"
              class="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition shadow-xs flex items-center gap-1.5 cursor-pointer"
            >
              <Plus class="w-3.5 h-3.5" />
              <span>Add Item Debit</span>
            </button>
          </div>

          <!-- Table of Adjustments -->
          <div class="border border-slate-200/80 dark:border-[#263449] rounded-2xl overflow-hidden bg-white dark:bg-[#18243A] shadow-xs">
            <div class="overflow-x-auto">
              <table class="w-full text-left text-xs min-w-[640px]">
                <thead class="bg-slate-50 dark:bg-[#111C30] text-slate-600 dark:text-[#A6B4C8] uppercase font-bold text-[11px] border-b border-slate-200/80 dark:border-[#263449]">
                  <tr>
                    <th class="py-3 px-4">Voucher</th>
                    <th class="py-3 px-4">Date</th>
                    <th class="py-3 px-4">Type</th>
                    <th class="py-3 px-4">Item / Reason</th>
                    <th class="py-3 px-4 text-right">Amount</th>
                    <th class="py-3 px-4 text-center">Status</th>
                    <th class="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 dark:divide-[#263449] font-medium">
                  <tr v-for="adj in adjustments" :key="adj._id" class="hover:bg-slate-50/70 dark:hover:bg-[#1A2840]/60">
                    <td class="py-3 px-4 font-mono font-bold text-indigo-600 dark:text-indigo-400">
                      {{ adj.voucherNumber }}
                    </td>
                    <td class="py-3 px-4 text-slate-500 dark:text-[#A6B4C8]">
                      {{ formatDateShort(adj.date) }}
                    </td>
                    <td class="py-3 px-4">
                      <span
                        class="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase inline-flex items-center gap-1"
                        :class="
                          adj.type === 'debit'
                            ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300'
                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                        "
                      >
                        {{ adj.type === 'debit' ? 'Debit (Charge)' : 'Credit (Waiver)' }}
                      </span>
                    </td>
                    <td class="py-3 px-4">
                      <div class="font-bold text-slate-800 dark:text-[#F1F5F9]">{{ adj.title }}</div>
                      <div v-if="adj.remarks" class="text-[11px] text-slate-400 truncate max-w-xs">
                        {{ adj.remarks }}
                      </div>
                    </td>
                    <td
                      class="py-3 px-4 text-right font-mono font-bold text-sm"
                      :class="adj.type === 'debit' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'"
                    >
                      {{ adj.type === 'debit' ? '+' : '-' }}₹{{ (adj.amount || 0).toLocaleString("en-IN") }}
                    </td>
                    <td class="py-3 px-4 text-center">
                      <span
                        class="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase inline-block"
                        :class="
                          adj.paymentStatus === 'paid'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                            : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                        "
                      >
                        {{ adj.paymentStatus }}
                      </span>
                    </td>
                    <td class="py-3 px-4 text-right">
                      <div class="flex items-center justify-end gap-1.5">
                        <button
                          v-if="adj.type === 'debit' && adj.paymentStatus !== 'paid'"
                          @click="$emit('pay-debit', adj)"
                          class="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition cursor-pointer"
                        >
                          Pay
                        </button>
                        <button
                          v-if="adj.receiptNumber"
                          @click="$emit('view-receipt', adj.receiptNumber)"
                          class="px-2 py-1 text-indigo-600 dark:text-indigo-400 text-xs font-bold cursor-pointer"
                        >
                          Receipt
                        </button>
                        <button
                          v-if="adj.paymentStatus !== 'paid'"
                          @click="$emit('delete-adjustment', adj._id)"
                          class="p-1 text-slate-400 hover:text-rose-600 rounded-lg transition cursor-pointer"
                          title="Delete adjustment"
                        >
                          <Trash2 class="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  <tr v-if="adjustments.length === 0">
                    <td colspan="7" class="py-6 text-center text-slate-400 text-xs">
                      No debit charges or concessions recorded.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- 7. VIEW 4: PAYMENT RECEIPTS TABLE -->
        <div v-else-if="activeView === 'transactions'" class="space-y-3">
          <div class="flex items-center justify-between">
            <h5 class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8] uppercase tracking-wider flex items-center gap-1.5">
              <Receipt class="w-4 h-4 text-emerald-500" />
              Recorded Payments & Receipts
            </h5>
            <span class="text-xs text-slate-400 dark:text-[#7E8DA5]">
              {{ transactions.length }} receipt{{ transactions.length > 1 ? "s" : "" }}
            </span>
          </div>

          <div class="border border-slate-200/80 dark:border-[#263449] rounded-2xl overflow-hidden bg-white dark:bg-[#18243A] shadow-xs">
            <div class="overflow-x-auto">
              <table class="w-full text-left text-xs min-w-[650px]">
                <thead class="bg-slate-50 dark:bg-[#111C30] text-slate-600 dark:text-[#A6B4C8] uppercase font-bold text-[11px] border-b border-slate-200/80 dark:border-[#263449]">
                  <tr>
                    <th class="py-3 px-4">Receipt No</th>
                    <th class="py-3 px-4">Date</th>
                    <th class="py-3 px-4">Category</th>
                    <th class="py-3 px-4">Period</th>
                    <th class="py-3 px-4">Method</th>
                    <th class="py-3 px-4 text-right">Amount Paid</th>
                    <th class="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 dark:divide-[#263449] font-medium">
                  <tr v-for="tx in transactions" :key="tx._id" class="hover:bg-slate-50/70 dark:hover:bg-[#1A2840]/60">
                    <td class="py-3 px-4 font-mono font-bold text-indigo-600 dark:text-indigo-400">
                      {{ tx.receiptNumber }}
                    </td>
                    <td class="py-3 px-4 text-slate-500 dark:text-[#A6B4C8]">
                      {{ formatDate(tx.paymentDate) }}
                    </td>
                    <td class="py-3 px-4 capitalize font-semibold text-slate-700 dark:text-[#F1F5F9]">
                      {{
                        tx.feeCategory === "admission"
                          ? "Admission"
                          : tx.feeCategory === "adjustment"
                          ? "Item Charge"
                          : "Monthly"
                      }}
                    </td>
                    <td class="py-3 px-4 text-slate-500 dark:text-[#A6B4C8]">
                      {{ tx.feeCategory === "monthly" && tx.monthName ? `${tx.monthName} ${tx.year}` : "One-Time" }}
                    </td>
                    <td class="py-3 px-4 text-slate-700 dark:text-[#F1F5F9]">
                      {{ tx.paymentMethod }}
                    </td>
                    <td class="py-3 px-4 text-right font-mono font-black text-emerald-600 dark:text-emerald-400 text-sm">
                      ₹{{ (tx.amount || 0).toLocaleString("en-IN") }}
                    </td>
                    <td class="py-3 px-4 text-right">
                      <button
                        @click="$emit('view-receipt', tx.receiptNumber)"
                        class="px-2.5 py-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                      >
                        Print Receipt
                      </button>
                    </td>
                  </tr>

                  <tr v-if="transactions.length === 0">
                    <td colspan="7" class="py-8 text-center text-slate-400 text-xs">
                      No payments recorded yet for this student.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div
        class="flex-shrink-0 px-4 sm:px-6 py-3 bg-slate-50 dark:bg-[#18243A] border-t border-slate-200/80 dark:border-[#263449] flex items-center justify-between"
      >
        <span class="text-xs text-slate-500 dark:text-[#A6B4C8] font-medium hidden sm:inline">
          {{ student?.name }} • {{ student?.class?.name || "N/A" }}
        </span>
        <div class="flex items-center gap-2 w-full sm:w-auto justify-end">
          <button
            @click="$emit('close')"
            class="flex-1 sm:flex-initial px-4 py-2 text-xs font-bold text-slate-700 dark:text-[#F1F5F9] bg-white dark:bg-[#111C30] hover:bg-slate-100 dark:hover:bg-[#1A2840] rounded-xl border border-slate-300 dark:border-[#263449] transition cursor-pointer"
          >
            Close
          </button>
          <button
            v-if="student"
            @click="$emit('record-payment', { student, feeStructure, monthlyRecords })"
            class="flex-1 sm:flex-initial px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs transition flex items-center justify-center gap-1.5 cursor-pointer"
          >
            <PlusCircle class="w-3.5 h-3.5" />
            <span>Record Payment</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed } from "vue";
  import {
    Wallet,
    X,
    Sliders,
    CalendarDays,
    CalendarRange,
    Receipt,
    PlusCircle,
    User,
    Scale,
    Plus,
    Trash2,
    CheckCircle2,
    AlertCircle,
    Award,
    CreditCard,
    History,
    Layers,
    GraduationCap,
  } from "lucide-vue-next";

  const props = defineProps({
    show: { type: Boolean, default: false },
    student: { type: Object, default: null },
    feeStructure: { type: Object, default: null },
    previousClassDues: { type: Object, default: null },
    lifetimeSummary: { type: Object, default: null },
    sessionBreakdown: { type: Array, default: () => [] },
    monthlySummary: { type: Object, default: () => ({}) },
    monthlyRecords: { type: Array, default: () => [] },
    transactions: { type: Array, default: () => [] },
    adjustments: { type: Array, default: () => [] },
    adjustmentSummary: { type: Object, default: () => ({}) },
    isLoading: { type: Boolean, default: false },
    sessionFeeHistory: { type: Array, default: () => [] },
    carryForwardRecords: { type: Array, default: () => [] },
    promotionHistory: { type: Array, default: () => [] },
  });

  const emit = defineEmits([
    "close",
    "configure",
    "record-payment",
    "view-receipt",
    "open-adjustment",
    "pay-debit",
    "delete-adjustment",
  ]);

  const activeView = ref("journey"); // "journey" | "monthly" | "adjustments" | "transactions"

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const viewMonthlyReceipt = (rec) => {
    const matchingTx = props.transactions.find(
      (tx) => tx.monthlyFeeRecordId === rec._id || (tx.month === rec.month && tx.year === rec.year)
    );
    if (matchingTx) {
      emit("view-receipt", matchingTx.receiptNumber);
    }
  };

  // Previous class dues (carried forward from earlier classes / sessions)
  const computedPreviousClassDues = computed(() => {
    const prev =
      props.previousClassDues ||
      props.feeStructure?.previousClassDues ||
      props.student?.previousClassDues ||
      {};
    const bal = Number(prev.balance) || 0;
    return {
      ...prev,
      hasPreviousDues: bal > 0,
      balance: bal,
    };
  });

  // Safe computed lifetime summary (never resets, aggregates from enrollment year to now)
  const computedLifetime = computed(() => {
    if (props.lifetimeSummary) return props.lifetimeSummary;

    const admExp = props.feeStructure?.admissionFee?.amount || 0;
    const admPaid = props.feeStructure?.admissionFee?.paidAmount || 0;
    const admBal = props.feeStructure?.admissionFee?.balance || 0;

    const moExp = (props.monthlyRecords || []).reduce((sum, r) => sum + (r.feeDue || 0), 0);
    const moPaid = (props.monthlyRecords || []).reduce((sum, r) => sum + (r.amountPaid || 0), 0);
    const moBal = (props.monthlyRecords || []).reduce((sum, r) => sum + (r.balance || 0), 0);

    const debExp = props.adjustmentSummary?.totalDebitAmount || 0;
    const debPaid = props.adjustmentSummary?.totalDebitPaid || 0;
    const debBal = props.adjustmentSummary?.totalDebitUnpaid || 0;

    const prevExp = computedPreviousClassDues.value.amount || computedPreviousClassDues.value.balance || 0;
    const prevPaid = computedPreviousClassDues.value.paidAmount || 0;
    const prevBal = computedPreviousClassDues.value.balance || 0;

    const totExp = admExp + moExp + debExp + prevExp;
    const totPaid = admPaid + moPaid + debPaid + prevPaid;
    const totOut = admBal + moBal + debBal + prevBal;

    const enrDate = props.student?.enrollmentDate || props.student?.createdAt;
    const enrYear = enrDate ? new Date(enrDate).getFullYear() : null;

    return {
      grandTotalExpected: totExp,
      grandTotalPaid: totPaid,
      grandTotalOutstanding: totOut,
      enrollmentDate: enrDate,
      enrolledYear: enrYear,
      currentSession: props.student?.session || props.feeStructure?.academicSession || "",
      currentClass: props.student?.class?.name || "N/A",
      collectionRate: totExp > 0 ? Number(((totPaid / totExp) * 100).toFixed(1)) : 0,
      hasUnclearedDues: totOut > 0,
    };
  });

  // Safe computed session fee history matching Section 3 grouped table
  const computedSessionFeeHistory = computed(() => {
    if (props.sessionFeeHistory && props.sessionFeeHistory.length > 0) {
      return props.sessionFeeHistory;
    }
    return computedSessionBreakdown.value.map((s) => {
      const items = [];
      const admExp = (s.session === computedLifetime.value.currentSession)
        ? (props.feeStructure?.admissionFee?.amount || 0)
        : 0;
      const admPaid = (s.session === computedLifetime.value.currentSession)
        ? (props.feeStructure?.admissionFee?.paidAmount || 0)
        : 0;
      const admBal = (s.session === computedLifetime.value.currentSession)
        ? (props.feeStructure?.admissionFee?.balance || 0)
        : 0;

      if (admExp > 0) {
        items.push({
          feeDescription: "Admission Fee",
          amount: admExp,
          paid: admPaid,
          pending: admBal,
          status: admBal === 0 ? "Paid" : (admPaid > 0 ? "Partial" : "Unpaid"),
        });
      }

      if (s.totalExpected > 0) {
        items.push({
          feeDescription: "Tuition Fee (" + s.monthsCount + " month" + (s.monthsCount !== 1 ? "s" : "") + ")",
          amount: s.totalExpected,
          paid: s.totalPaid,
          pending: s.totalOutstanding,
          status: s.totalOutstanding === 0 ? "Paid" : (s.totalPaid > 0 ? "Partial" : "Unpaid"),
        });
      }

      return {
        session: s.session,
        className: s.className,
        isCurrent: s.isCurrent,
        items,
        totalAmount: s.totalExpected + admExp,
        totalPaid: s.totalPaid + admPaid,
        totalPending: s.totalOutstanding + admBal,
        status: (s.totalOutstanding + admBal) === 0 ? "Paid" : "Pending",
      };
    });
  });

  // Safe computed session breakdown (e.g. Class 1 Session 2020-2021 paid, left, unpaid months)
  const computedSessionBreakdown = computed(() => {
    if (props.sessionBreakdown && props.sessionBreakdown.length > 0) {
      return props.sessionBreakdown;
    }

    const map = new Map();
    const curSess = props.student?.session || props.feeStructure?.academicSession || "";

    (props.monthlyRecords || []).forEach((rec) => {
      let sess = (rec.academicSession || "").trim();
      if (!sess) {
        sess = rec.month >= 4 ? `${rec.year}-${rec.year + 1}` : `${rec.year - 1}-${rec.year}`;
      }

      if (!map.has(sess)) {
        map.set(sess, {
          session: sess,
          className: props.student?.class?.name
            ? `${props.student.class.name}${props.student.class.section ? " - " + props.student.class.section : ""}`
            : "Class",
          totalExpected: 0,
          totalPaid: 0,
          totalOutstanding: 0,
          monthsCount: 0,
          paidMonthsCount: 0,
          unpaidMonthsCount: 0,
          unpaidMonths: [],
          isCurrent: sess === curSess,
        });
      }

      const s = map.get(sess);
      s.totalExpected += rec.feeDue || 0;
      s.totalPaid += rec.amountPaid || 0;
      s.totalOutstanding += rec.balance || 0;
      s.monthsCount += 1;

      if (rec.balance <= 0 && rec.amountPaid > 0) {
        s.paidMonthsCount += 1;
      } else if (rec.balance > 0) {
        s.unpaidMonthsCount += 1;
        s.unpaidMonths.push({
          month: rec.month,
          year: rec.year,
          monthName: rec.monthName || (months[rec.month - 1] || `Month ${rec.month}`),
          feeDue: rec.feeDue,
          amountPaid: rec.amountPaid,
          balance: rec.balance,
          status: rec.status,
        });
      }
    });

    return Array.from(map.values())
      .map((s) => ({
        ...s,
        status: s.totalOutstanding === 0 ? "cleared" : "pending",
        collectionRate:
          s.totalExpected > 0
            ? Number(((s.totalPaid / s.totalExpected) * 100).toFixed(1))
            : 100,
      }))
      .sort((a, b) => b.session.localeCompare(a.session));
  });
</script>
